import { useEffect, useState } from "react";
import { DataGrid, GridColDef, GridToolbar } from "@mui/x-data-grid";
import { Box, Tab, Tabs, Button, Chip } from "@mui/material";
import { useOrders } from "../hooks/useOrders";
import { useLivePrices } from "../hooks/useLivePrices";
import { calculatePnlPercentage, formatPrice } from "../utils/utils";
import TradingSymbol from "components/TradingSymbol";
import { useAuth } from "../hooks/useAuth";
import ZerodhaAuthAlert from "../components/ZerodhaAuthAlert";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

export default function OrdersPage() {
  const { fetchLatestOrders, fetchLatestGttOrders } = useOrders();
  const { prices, getLivePrice } = useLivePrices();
  const [orders, setOrders] = useState<any[]>([]);
  const [gttOrders, setGttOrders] = useState<any[]>([]);
  const [value, setValue] = useState(0);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const { user } = useAuth();

  useEffect(() => {
    if (user?.isZerodhaLinked) {
      fetchLatestOrders().then((data) => {
        setOrders(data || []);
      });
      fetchLatestGttOrders().then((data) => {
        setGttOrders(data || []);
      });
    }
  }, [user?.isZerodhaLinked]);

  useEffect(() => {
    const tokens = [
      ...orders.map((o) => o.instrument_token),
      ...gttOrders.map((g) => g.condition.instrument_token),
    ].filter((token) => token);
    if (tokens.length > 0) getLivePrice(tokens);
  }, [orders, gttOrders]);

  const ordersColumns: GridColDef[] = [
    {
      field: "tradingsymbol",
      headerName: "Symbol",
      flex: 0.5,
      renderCell: (params) => (
        <TradingSymbol symbol={params.row.tradingsymbol} />
      ),
    },
    {
      field: "quantity",
      headerName: "Qty",
      type: "number",
      flex: 0.5,
    },
    {
      field: "average_price",
      headerName: "Avg Price",
      type: "number",
      flex: 0.5,
      renderCell: (params) => (
      <span>{formatPrice(params.row.average_price)}</span>
      )
    },
    {
      field: "current_price",
      headerName: "Current Price",
      type: "number",
      flex: 0.5,
      renderCell: (params) => {
        const livePrice = prices[params.row.instrument_token]?.price;
        return (
          <span
            className={
              params.row.average_price > livePrice
                ? "priceDown"
                : "priceUp"
            }
          >
            {livePrice || "-"}
          </span>
        );
      },
    },
    {
      field: "percentage",
      headerName: "Percentage",
      type: "number",
      flex: 0.5,
      renderCell: (params) => {
        const livePrice = prices[params.row.instrument_token]?.price;
        const pnl = calculatePnlPercentage(
          livePrice,
          params.row.average_price
        );
        return (
          <span className={Number(pnl) > 0 ? "percentUp" : "percentDown"}>
            {pnl}
          </span>
        );
      },
    },
    {
      field: "transaction_type",
      headerName: "Type",
      flex: 0.5,
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.5,
      renderCell: (params) => (
        <Button variant="outlined" size="small" color="error">
          Cancel
        </Button>
      ),
    },
  ];

  const gttColumns: GridColDef[] = [
    {
      field: "tradingsymbol",
      headerName: "Symbol",
      flex: 0.5,
      renderCell: (params) => (
        <TradingSymbol symbol={params.row.condition.tradingsymbol} />
      ),
    },
    {
      field: "quantity",
      headerName: "Qty",
      type: "number",
      flex: 0.5,
      renderCell: (params) => <>{params.row.orders[0].quantity}</>,
    },
    {
      field: "last_price",
      headerName: "Price",
      type: "number",
      flex: 0.5,
      renderCell: (params) => <>{params.row.condition.last_price}</>,
    },
    {
      field: "stop_loss",
      headerName: "Stop Loss",
      type: "number",
      flex: 0.5,
      renderCell: (params) => <>{params.row.condition.trigger_values[0]}</>,
    },
    {
      field: "target_price",
      headerName: "Target Price",
      type: "number",
      flex: 0.5,
      renderCell: (params) => <>{params.row.condition.trigger_values[1]}</>,
    },
    {
      field: "current_price",
      headerName: "Current Price",
      type: "number",
      flex: 0.5,
      renderCell: (params) => {
        const livePrice = prices[params.row.condition.instrument_token]?.price;
        return (
          <span
            className={
              livePrice > params.row.condition.last_price
                ? "priceUp"
                : "priceDown"
            }
          >
            {livePrice || "-"}
          </span>
        );
      },
    },
    {
      field: "percentage",
      headerName: "Percentage",
      type: "number",
      flex: 0.5,
      renderCell: (params) => {
        const livePrice = prices[params.row.condition.instrument_token]?.price;
        const pnl = calculatePnlPercentage(
          livePrice,
          params.row.condition.last_price
        );
        return (
          <span className={Number(pnl) > 0 ? "percentUp" : "percentDown"}>
            {pnl}
          </span>
        );
      },
    },
    {
      field: "transaction_type",
      headerName: "Type",
      flex: 0.5,
      renderCell: (params) => <>{params.row.orders[0].transaction_type}</>,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.5,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          size="small"
          color={params.row.status === "active" ? "primary" : "success"}
        />
      ),
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0.5,
      renderCell: (params) => (
        <Button variant="outlined" size="small" color="error">
          Cancel
        </Button>
      ),
    },
  ];

  const ordersData = orders.map((order) => ({
    ...order,
    id: order.order_id,
  }));

  const gttData = gttOrders.map((gttOrder) => ({
    ...gttOrder,
    id: gttOrder.id,
  }));

  return (
    <Box sx={{ width: "98%" }}>
      {!user?.isZerodhaLinked && <ZerodhaAuthAlert />}
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={value} onChange={handleChange} aria-label="orders tabs">
          <Tab label="Orders" {...a11yProps(0)} />
          <Tab label="GTT" {...a11yProps(1)} />
        </Tabs>
      </Box>
      <CustomTabPanel value={value} index={0}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h4 className="text-2xl font-bold mb-4">Todays Orders</h4>
          </div>
          {/* <Typography variant="h5">Todays Orders</Typography> */}
          <DataGrid
            rows={ordersData}
            columns={ordersColumns}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
              sorting: { sortModel: [{ field: "percentage", sort: "desc" }] },
            }}
            pageSizeOptions={[10, 25, 50]}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "primary.main",
                width: "auto",
              },
            }}
          />
        </div>
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <h4 className="text-2xl font-bold mb-4">GTT Orders</h4>
          </div>
          <DataGrid
            rows={gttData}
            columns={gttColumns}
            getRowId={(row) => row.id}
            slots={{ toolbar: GridToolbar }}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 10 } },
              sorting: { sortModel: [{ field: "percentage", sort: "desc" }] },
            }}
            pageSizeOptions={[10, 25, 50]}
            sx={{
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "primary.main",
                width: "auto",
              },
            }}
          />
        </div>
      </CustomTabPanel>
    </Box>
  );
}
